import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  Spacer,
  Text,
  Tooltip,
  useBreakpointValue,
  useColorMode,
} from "@chakra-ui/react";
import { toast } from "react-toastify";
import AgentDomain from "entities/domain/agents/agent-domain";
import ConversationDomain from "entities/domain/conversations/conversation-domain";
import MessageDomain, {
  MessageStatus as MessageDomainStatus,
} from "entities/domain/conversations/message-domain";
import useConversationsStore from "hooks/use-conversations-store";
import useAgentsStore from "hooks/use-agents-store";
import FuzeyPopover, { PopoverAction } from "components/shared/FuzeyPopover";
import { ReactComponent as CancelIcon } from "assets/icons/Close-ds.svg";
import { ReactComponent as SendIcon } from "assets/icons/Send-arrow-ds.svg";
import { ReactComponent as EditIcon } from "assets/icons/edit-ds.svg";
import useCustomChakraTheme from "hooks/use-custom-chakra-theme";
import useAnalytics from "hooks/use-analytics";
import useMerchantStore from "hooks/use-merchant-store";
import { calculateTypingTime, isFuzeyMerchant } from "util/methods";
import MessageStatus from "./MessageStatus";

const getRandomCallToActionMessage = () => {
  const possibleCTAs = [
    "Save time with AI-generated response",
    "Increase your productivity with auto-generated reply",
    "Maximize efficiency! Use the suggestion made by AI",
    "Respond faster! Auto replies are your key",
    "Auto reply: your 24/7 customer service solution",
    "Try auto reply, enhance your customer interactions",
    "Level up with auto replies. Use this one",
    "Cut response times! Send auto replies",
    "Be a pro in customer service. Use auto replies",
    "Stay ahead with quick auto replies. Try now",
    "Auto replies: Your secret to effective communication",
    "Boost your customer service. Use auto replies",
    "No more delayed responses! Use auto replies",
    "Stay responsive, try auto replies today",
    "Unleash the power of auto replies. Try now",
    "Auto replies: your tool for instant customer support",
    "Drive engagement with speedy auto replies. Try now",
  ];

  return possibleCTAs[Math.floor(Math.random() * possibleCTAs.length)];
};

const getMessageTitleColor = (
  messageStatus: MessageDomainStatus,
  isReceiving: boolean,
  colorMode: "light" | "dark",
  colorScheme: string
) => {
  let color = "gray.50";

  if (messageStatus === MessageDomainStatus.DRAFT) {
    if (colorMode === "light") {
      color = "gray.300";
    } else {
      color = "gray.600";
    }

    return color;
  }

  if (isReceiving) {
    if (colorMode === "light") {
      color = "gray.400";
    } else {
      color = "gray.300";
    }

    return color;
  }

  if (colorMode === "light") {
    color = `${colorScheme}.700`;
  } else {
    color = `${colorScheme}.600`;
  }

  return color;
};

export default ({
  children,
  isReceiving,
  agent,
  activeConversation,
  message,
}: React.PropsWithChildren<{
  isReceiving: boolean;
  message: MessageDomain;
  agent?: AgentDomain;
  activeConversation?: ConversationDomain;
}>) => {
  const { merchant } = useMerchantStore();
  const { trackAnalytics } = useAnalytics();
  const {
    markConversationAsRead,
    markConversationAsUnread,
    sendDraftMessage,
    deleteDraftMessage,
    setAutoReplySuggestion,
  } = useConversationsStore();
  const { currentAgent } = useAgentsStore();
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const [popoverActions, setPopoverActions] = useState<PopoverAction[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [ctaMessage, setCtaMessage] = useState<string>();
  const [draftActionPressed, setDraftActionPressed] = useState<boolean>(false);

  useEffect(() => {
    setCtaMessage(getRandomCallToActionMessage());
  }, []);

  useEffect(() => {
    const newPopoverActions: PopoverAction[] = [];

    if (!activeConversation || !message) {
      setPopoverActions(newPopoverActions);

      return;
    }

    newPopoverActions.push(
      message.isRead
        ? {
            name: "Mark Unread",
            callback: () => {
              markConversationAsUnread(
                activeConversation.id,
                message.id,
                currentAgent.id
              );
            },
          }
        : {
            name: "Mark Read",
            callback: () => {
              markConversationAsRead(
                activeConversation.id,
                message.id,
                currentAgent.id
              );
            },
          }
    );

    setPopoverActions(newPopoverActions);
  }, [activeConversation, currentAgent, message]);

  const { colorMode } = useColorMode();
  const { colorScheme } = useCustomChakraTheme();

  const [messageTitleColor, setMessageTitleColor] = useState(
    getMessageTitleColor(message.status, isReceiving, colorMode, colorScheme)
  );

  useEffect(() => {
    setMessageTitleColor(
      getMessageTitleColor(message.status, isReceiving, colorMode, colorScheme)
    );
  }, [colorMode, colorScheme, isReceiving, message.status]);

  return (
    <Flex
      direction="column"
      color={colorMode === "dark" ? "gray.50" : "gray.900"}
      fontSize="smaller"
    >
      {message.status !== MessageDomainStatus.DRAFT ? (
        <Flex alignItems="center">
          <Text fontWeight={700} fontSize="md" color={messageTitleColor}>
            {message.isIncoming
              ? activeConversation?.customer.getDisplayName()
              : agent?.getFullName()}
          </Text>
          <Spacer />
          {message.isIncoming && <FuzeyPopover actions={popoverActions} />}
        </Flex>
      ) : (
        <Flex alignItems="center" borderBottomWidth="1px" p={2}>
          <Text fontSize="md" color={messageTitleColor} mx="auto">
            {ctaMessage}
          </Text>
        </Flex>
      )}
      {message.status !== MessageDomainStatus.DRAFT ? (
        <>{children}</>
      ) : (
        <Box py={2} px={3}>
          {children}
        </Box>
      )}
      {message.status !== MessageDomainStatus.DRAFT ? (
        <Flex direction="row-reverse" alignItems="center" color="#9A9BB5">
          {activeConversation &&
          message.getStatusIcon(activeConversation?.channel) === "failed" ? (
            <Box mr={1}>
              <MessageStatus
                activeConversation={activeConversation}
                message={message}
              />
            </Box>
          ) : (
            <>
              <Box mr={1}>
                <MessageStatus
                  activeConversation={activeConversation}
                  message={message}
                />
              </Box>
              <Text id="message-time-stamp" color={messageTitleColor}>
                {message.getTimeStamp()}
              </Text>
            </>
          )}
        </Flex>
      ) : (
        <ButtonGroup size="sm" isAttached variant="outline" w="100%">
          <Tooltip label="Don't show this draft again" isDisabled={isBaseSize}>
            <Button
              borderTopLeftRadius="0"
              borderBottomLeftRadius="lg"
              borderWidth={0}
              w="33.33%"
              colorScheme="red"
              bgColor={colorMode === "dark" ? "red.800" : "red.100"}
              py={3}
              height="auto"
              isDisabled={draftActionPressed}
              onClick={() => {
                trackAnalytics("delete_draft_message", {
                  merchant_id: merchant.id,
                  agent_id: currentAgent?.id,
                  distinct_id: `merchant:${merchant.id}`,
                  motive: "inbox",
                  conversation_id: activeConversation?.id,
                });
                setDraftActionPressed(true);
                deleteDraftMessage(message.id);
              }}
            >
              <Icon
                as={CancelIcon}
                __css={{
                  rect: {
                    fill: "inherit",
                  },
                  path: {
                    fill: colorMode === "dark" ? "red.200" : "red.500",
                  },
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip label="Edit this draft" isDisabled={isBaseSize}>
            <Button
              borderRadius="0"
              borderWidth={0}
              w="33.33%"
              colorScheme={colorScheme}
              bgColor={colorMode === "dark" ? `orange.800` : `orange.100`}
              py={3}
              height="auto"
              isDisabled={draftActionPressed}
              onClick={() => {
                trackAnalytics("edit_draft_message", {
                  merchant_id: merchant.id,
                  agent_id: currentAgent?.id,
                  distinct_id: `merchant:${merchant.id}`,
                  motive: "inbox",
                  conversation_id: activeConversation?.id,
                });
                setDraftActionPressed(true);
                setAutoReplySuggestion(message.body);
                deleteDraftMessage(message.id);
              }}
            >
              <Icon
                as={EditIcon}
                __css={{
                  rect: {
                    fill: "inherit",
                  },
                  path: {
                    fill: colorMode === "dark" ? "orange.200" : "orange.500",
                  },
                }}
              />
            </Button>
          </Tooltip>
          <Tooltip
            label="Send a message with this draft"
            isDisabled={isBaseSize}
          >
            <Button
              borderTopRightRadius="0"
              borderBottomRightRadius="lg"
              borderWidth={0}
              w="33.33%"
              colorScheme={colorScheme}
              bgColor={
                colorMode === "dark"
                  ? `${colorScheme}.800`
                  : `${colorScheme}.100`
              }
              py={3}
              height="auto"
              isDisabled={draftActionPressed}
              onClick={() => {
                trackAnalytics("send_draft_message", {
                  merchant_id: merchant.id,
                  agent_id: currentAgent?.id,
                  distinct_id: `merchant:${merchant.id}`,
                  motive: "inbox",
                  conversation_id: activeConversation?.id,
                });
                setDraftActionPressed(true);
                sendDraftMessage(message.id, message.conversationId);
                toast.success(
                  `You've saved at least ${calculateTypingTime(message.body)}!`
                );
              }}
            >
              <Icon
                as={SendIcon}
                __css={{
                  rect: {
                    fill: "inherit",
                  },
                  path: {
                    fill:
                      colorMode === "dark"
                        ? `${colorScheme}.200`
                        : `${colorScheme}.500`,
                  },
                }}
              />
            </Button>
          </Tooltip>
        </ButtonGroup>
      )}
    </Flex>
  );
};
